// Generated with util/create-component.js
import React, { ReactNode } from "react";
import { StyledCard, CardContent, CardContentHeader, CardIconContaner, StyledIcon, CheckmarkContainer, BarFooter, CardContainer, CardContentWrapper, CardImg } from "./Card.styles";
import lock from "./lock.svg";
import checkmark from "./checkmark.svg";
import downArrow from "./downArrow.svg";
import upArrow from "./upArrow.svg";
import { StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";
import { FormWrapper } from "../Forms/FormWrapper";

type CardProps = {
  children: ReactNode;
  footer?: string;
  handleClick?: () => void;
  size?: "sm" | "lg";
};

/**
 * Card Component
 *
 * This component renders a card with optional header and footer.
 *
 * @component
 *
 * @param {Object} CardProps - The props for the Card component.
 * @param {ReactNode} props.children - The content to be displayed within the card.
 * @param {string} [props.footer] - Optional footer text to display at the bottom of the card.
 * @param {function} [props.handleClick] - Optional callback function to handle click events on the card.
 * @param {"sm" | "lg"} [props.size="lg"] - Optional size of the card, can be "sm" (small) or "lg" (large).
 *
 * @returns {JSX.Element} Rendered Card component.
 */

const Card: React.FC<CardProps> = ({ footer, handleClick, size = "lg", children }: CardProps) => {
  return (
    <StyleSheetManager shouldForwardProp={isPropValid}>
      <CardContainer onClick={handleClick}>
        <StyledCard hasFooter={footer?.length > 0 ? true : false} size={size}>
          {children}
        </StyledCard>
        {footer && <BarFooter>{footer}</BarFooter>}
      </CardContainer>
    </StyleSheetManager>
  );
};

type CardContentContainerProps = {
  header?: string;
  child?: ReactNode;
  expandedChild?: ReactNode;
  showExpanded?: boolean;
  imgUrl?: string;
};

/**
 * Card Content Container Component
 *
 * This component represents the content container of a card, including header and text.
 *
 * @component
 *
 * @param {Object} CardContentContainerProps - The props for the CardContentContainer component.
 * @param {string} props.child - The main child content to display in the card.
 * @param {string} [props.header] - Optional header text to display at the top of the card content.
 * @param {string} [props.expandedChild] - Optional expanded child content to display in the card.
 * @param {boolean} [props.showExpandedText] - Optional flag to control the visibility of the expanded text.
 *
 * @returns {JSX.Element} Rendered CardContentContainer component.
 */

const CardContentContainer: React.FC<CardContentContainerProps> = ({ header, child, expandedChild, showExpanded, imgUrl }) => {
  return (
    <CardContentWrapper>
      {imgUrl && <CardImg src={imgUrl} alt="team-image" />}
      <CardContent>
        {header && <CardContentHeader>{header}</CardContentHeader>}
        {child}
        {showExpanded && expandedChild && <div>{expandedChild}</div>}
      </CardContent>
    </CardContentWrapper>
  );
};

type CardIconContainerProps = {
  children?: ReactNode;
  showCheckmark?: boolean;
  showLockIcon?: boolean;
  showUpArrow?: boolean;
  showDownArrow?: boolean;
  checkBox?: {
    checked: boolean;
    onChange: () => void;
  };
};

/**
 * Card Icon Container Component
 *
 * This component represents the container for icons and additional content within a card.
 *
 * @component
 *
 * @param {Object} CardIconContainerProps - The props for the CardIconContainer component.
 * @param {ReactNode} [props.children] - Optional children elements to include within the container.
 * @param {boolean} [props.showCheckmark] - Optional flag to display a checkmark icon and "Active" text.
 * @param {boolean} [props.showLockIcon] - Optional flag to display a lock icon.
 * @param {boolean} [props.showUpArrow] - Optional flag to display an up-arrow icon.
 * @param {boolean} [props.showDownArrow] - Optional flag to display a down-arrow icon.
 *
 * @returns {JSX.Element} Rendered CardIconContainer component.
 */

const CardIconContainer: React.FC<CardIconContainerProps> = ({ showCheckmark, showLockIcon, showUpArrow, showDownArrow, checkBox, children }) => {
  return (
    <FormWrapper>
      <CardIconContaner>
        {children}
        {checkBox && (
          <div className="form-check form-switch">
            <input
              className="form-check-input card-checkbox-color"
              type="checkbox"
              id="flexSwitchCheckDefault"
              checked={checkBox.checked}
              onChange={() => {
                checkBox.onChange();
              }}
            />
          </div>
        )}

        {showCheckmark && (
          <CheckmarkContainer>
            <StyledIcon src={checkmark} alt="checkmark" />
            <>Active</>
          </CheckmarkContainer>
        )}
        {showLockIcon && <StyledIcon src={lock} alt="lock" />}
        {showUpArrow && <StyledIcon src={upArrow} alt="up-arrow" />}
        {showDownArrow && <StyledIcon src={downArrow} alt="down-arrow" />}
      </CardIconContaner>
    </FormWrapper>
  );
};

export default { Card, CardContentContainer, CardIconContainer };
